import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Avatar, Row, Col, Input, message, Popover, Checkbox, Badge, Tooltip } from 'antd'
import { SearchOutlined, DownloadOutlined, FilterOutlined, CheckOutlined } from '@ant-design/icons'
import { COLORS } from 'theme'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import Select from 'components/Select'
import TreeSelect from 'components/TreeSelect'
import Pagination from 'components/Pagination'
import Button from 'components/Button'
import DownloadButton from 'components/DownloadButton'
import {
  SearchSection,
  ShowingText,
  TableTop,
} from '../../../Style/style'
import { StyledTable } from 'components/TableStyles/TableStyles'
import {
  getYearOfPassing,
  exportTpoStudentListToExcel,
  exportStudentData,
} from '../../../actions'
import StudentReportModal from '../../StudentReport'
import { resolveRoleBasedSectionColumns } from '../../roleBasedColumns'
import { ATTEMPT_STATUS, ATTEMPT_STATUS_TOOLTIP, resolveAttemptStatus } from '../../attemptStatus'

import {
  CandidateListWrapper,
  CandidateInfo,
  NoScrollbarX,
  CandidateName,
  CandidateId,
  ContactInfo,
  CefrLevel,
  PrimaryCefr,
  SecondaryCefr,
  NoData,
  HeaderFullWidth,
  MultiPadding,
  BoldText,
  FilterMenuWrapper,
  FilterFooter,
  SpaceBetweenRow,
  StyledFilterCol,
  FilterLabel,
  StyledFormItem,
  LevelBadge,
} from './styles'

// Enhanced styled components for the filter container
const FilterInlineContainer = styled.div`
  padding: 24px 0;
  margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);

  @media (max-width: 1200px) {
    padding: 20px 0;
  }
`

// Changed from grid to flexbox for inline layout
const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;
  // padding: 0 24px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 0 16px;
  }
`

// Changed from column to row for inline layout
const FilterGroup = styled.div`
  display: flex;
  align-items: baseline; /* Changed from center to baseline for better text alignment */
  gap: 8px;

  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-select,
  .ant-tree-select {
    border-radius: 8px;
    border: 1px solid #d1d5db;
    transition: all 0.2s ease;
    min-width: 160px; /* Reduced min-width for better inline layout */
    max-width: 180px; /* Added max-width to prevent overflow */

    &:hover {
      border-color: #3249d7;
      box-shadow: 0 0 0 2px rgba(50, 73, 215, 0.1);
    }

    &:focus,
    &.ant-select-focused,
    &.ant-tree-select-focused {
      border-color: #3249d7;
      box-shadow: 0 0 0 3px rgba(50, 73, 215, 0.15);
    }
  }
`

const EnhancedFilterLabel = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap; /* Prevent label from wrapping */
`

const FilterSection = styled.div`
  margin-bottom: 0px;
`

const StyledSearchInput = styled(Input)`
  height: 48px;
  border: none;
  box-shadow: none !important;

  &:focus,
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border: none;
    box-shadow: none !important;
  }

  .ant-input {
    height: 100%;
    border: none;
    box-shadow: none !important;

    &:focus {
      box-shadow: none !important;
    }
  }
`

// Override the negative margins from shared styles to properly align with table
const AlignedTableTop = styled(TableTop)`
  margin-bottom: 0;

  ${SearchSection} {
    margin-bottom: 0;
  }

  ${ShowingText} {
    margin-bottom: 0;
  }
`

const StatusFilterTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 14px;
  margin-left: 12px;
  background: #ffffff;
  border: 1px solid ${props => (props.$active ? '#3249d7' : '#d1d5db')};
  color: ${props => (props.$active ? '#3249d7' : '#374151')};
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${props =>
    props.$active ? '0 0 0 3px rgba(50, 73, 215, 0.12)' : 'none'};

  &:hover {
    border-color: #3249d7;
    color: #3249d7;
  }

  .anticon {
    font-size: 14px;
  }
`

const StatusPopoverBody = styled.div`
  width: 240px;
  padding: 6px 4px 4px;
  background: #ffffff;
  color: #111827;

  .status-option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-radius: 6px;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s ease;

    &:hover {
      background: #f3f4f6;
    }

    .label {
      font-size: 13px;
      color: #374151;
      font-weight: 500;
    }
  }
`

const StatusPopoverFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #e5e7eb;
  padding: 8px 6px 4px;
  margin-top: 4px;

  button {
    background: transparent;
    border: none;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;

    &.clear {
      color: #6b7280;
      &:hover { color: #111827; background: #f3f4f6; }
    }
    &.apply {
      color: #ffffff;
      background: #3249d7;
      padding: 6px 14px;
      &:hover { background: #2538b5; }
    }
  }
`

// Values are the backend filter enums; labels are the corporate vocabulary.
const STATUS_OPTIONS = [
  { value: 'COMPLETED', label: ATTEMPT_STATUS.completed.label },
  { value: 'PENDING', label: ATTEMPT_STATUS.pending.label },
  { value: 'INPROGRESS', label: ATTEMPT_STATUS.inProgress.label },
  { value: 'DROPOUT', label: ATTEMPT_STATUS.dropped.label },
]
const STATUS_LABEL_MAP = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label
  return acc
}, {})

// Styled table wrapper using admin-react's StyledTable
const StyledTableSqare = styled(StyledTable)`
  .ant-table {
    border-radius: 8px;
  }
`

// Helper to structure data for TreeSelect with a "Select All" root node
// Filters out invalid entries (empty, dashes, etc.)
const buildTreeData = (data, selectAllLabel = 'Select All') => {
  if (!data || data.length === 0) {
    return []
  }

  // Filter out invalid entries (empty names, dashes, etc.)
  const validData = data.filter(item => {
    const name = item?.name || item?.label || ''
    const trimmedName = String(name).trim()
    return trimmedName && trimmedName !== '-' && trimmedName !== '---' && trimmedName.toLowerCase() !== 'null'
  })

  if (validData.length === 0) {
    return []
  }

  const children = validData.map(item => ({
    title: item.name || item.label,
    value: item.id || item.value,
    key: item.id || item.value,
  }))
  return [
    {
      title: selectAllLabel,
      value: 'all',
      key: 'all',
      children: children,
    },
  ]
}

const CandidateList = ({
  data,
  loading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onCandidateClick,
  // filter props
  topLevelDomain,
  setTopLevelDomain,
  topLevelDegree,
  setTopLevelDegree,
  topLevelDepartment,
  setTopLevelDepartment,
  topLevelSpecialization,
  setTopLevelSpecialization,
  topLevelPassingYear,
  setTopLevelPassingYear,
  searchTerm,
  setSearchTerm,
  // chart filter props
  chartFilters,
  // options props
  domainOptionsFromProps = [],
  degreeOptionsFromProps = [],
  departmentOptionsFromProps = [],
  specializationOptionsFromProps = [],
  instituteId,
  // assessment context
  assessmentType,
  assessmentId,
  enabledSections,
  roleBasedSections = [], // Role-based sections selected at creation (count > 0)
  // NEW: Sort change callback for server-side sorting
  onSortChange,
  // Schedule info for header display
  scheduleName,
  scheduleNumber,
  // Status filter (shown for non-diagnosis assessments only)
  statusFilter = [],
  setStatusFilter,
  showStatusFilter = false,
}) => {
  const dispatch = useDispatch()
  // State for TableSearchFilter menu
  const [domain, setDomain] = useState([])
  const [degree, setDegree] = useState([])
  const [department, setDepartment] = useState([])
  const [specialization, setSpecialization] = useState([])
  const [passingYear, setPassingYear] = useState([])

  const [sortBy, setSortBy] = useState({})

  // Temp filters for menu
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [tempDomain, setTempDomain] = useState([])
  const [tempDegree, setTempDegree] = useState([])
  const [tempDepartment, setTempDepartment] = useState([])
  const [tempSpecialization, setTempSpecialization] = useState([])
  const [tempPassingYear, setTempPassingYear] = useState([])

  const [multiSelect, setMultiSelect] = useState([])

  // Local state for debounced search input
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || '')
  const debounceTimerRef = useRef(null)

  // State for StudentReportDrawer
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState(null)

  // Status filter popover state
  const [statusPopoverOpen, setStatusPopoverOpen] = useState(false)
  const [tempStatusFilter, setTempStatusFilter] = useState(statusFilter || [])

  useEffect(() => {
    setTempStatusFilter(statusFilter || [])
  }, [statusFilter, statusPopoverOpen])

  useEffect(() => {
    if (!statusPopoverOpen) return
    const onScroll = () => setStatusPopoverOpen(false)
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [statusPopoverOpen])

  const toggleTempStatus = (value) => {
    setTempStatusFilter(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    )
  }

  const applyStatusFilter = () => {
    if (typeof setStatusFilter === 'function') {
      setStatusFilter(tempStatusFilter)
    }
    if (onPageChange) onPageChange(1)
    setStatusPopoverOpen(false)
  }

  const clearStatusFilter = () => {
    setTempStatusFilter([])
    if (typeof setStatusFilter === 'function') {
      setStatusFilter([])
    }
    if (onPageChange) onPageChange(1)
    setStatusPopoverOpen(false)
  }

  // Sync local search term when parent searchTerm changes (e.g., on clear)
  useEffect(() => {
    setLocalSearchTerm(searchTerm || '')
  }, [searchTerm])

  // Debounced search handler - waits 500ms after user stops typing
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value
    setLocalSearchTerm(value)

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer to update parent state after 500ms
    debounceTimerRef.current = setTimeout(() => {
      if (onPageChange) onPageChange(1)
      setSearchTerm(value)
    }, 500)
  }, [setSearchTerm, onPageChange])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Export handler
  const isStandardExport = ['communication', 'aptitude'].includes((assessmentType || '').toLowerCase())

  const handleExport = useCallback(async () => {
    try {
      message.loading({ content: 'Preparing export...', key: 'export' })

      if (isStandardExport) {
        await dispatch(exportTpoStudentListToExcel({
          instituteId,
          searchParams: searchTerm,
          passingYear: topLevelPassingYear,
          domain: topLevelDomain?.map(d => d.value || d) || [],
          degree: topLevelDegree?.map(d => d.value || d) || [],
          department: topLevelDepartment?.map(d => d.value || d) || [],
          specialization: topLevelSpecialization?.map(s => s.value || s) || [],
          sortBy: sortBy && sortBy.field ? sortBy : { field: 'name', order: 'asc' }
        }))
      } else {
        // Other types: export the full ("sent") list, reflecting the same
        // degree/department/specialization/status/search filters as the table.
        await dispatch(exportStudentData(assessmentId, 'college', 'sent', {
          degree: topLevelDegree?.map(d => d.label || d.value || d).filter(v => v !== 'all') || [],
          department: topLevelDepartment?.map(d => d.label || d.value || d).filter(v => v !== 'all') || [],
          specialization: topLevelSpecialization?.map(s => s.label || s.value || s).filter(v => v !== 'all') || [],
          status: showStatusFilter ? (statusFilter || []) : [],
          search: searchTerm || '',
        }))
      }

      message.success({ content: 'Export completed!', key: 'export' })
    } catch (error) {
      console.error('Export failed:', error)
      message.error({ content: 'Export failed. Please try again.', key: 'export' })
    }
  }, [dispatch, isStandardExport, instituteId, assessmentId, searchTerm, topLevelPassingYear, topLevelDomain, topLevelDegree, topLevelDepartment, topLevelSpecialization, statusFilter, showStatusFilter, sortBy])

  const yearOptions = [
    { value: '2023', label: '2023' },
    { value: '2024', label: '2024' },
    { value: '2025', label: '2025' },
  ]
  const mapOptions = options =>
    (options || []).map(o => ({
      title: o.label || o.name,
      value: o.value || o.id,
    }))

  const treeDomainOptions = mapOptions(domainOptionsFromProps)
  const treeDegreeOptions = mapOptions(degreeOptionsFromProps)
  const treeDepartmentOptions = mapOptions(departmentOptionsFromProps)
  const treeSpecializationOptions = mapOptions(specializationOptionsFromProps)
  const yearDataForSelect = yearOptions.map(o => ({ year: o.value }))

  // Helper function to check if a value is empty/null/undefined
  const isEmpty = (value) => {
    return value === undefined || value === null || value === '' ||
      value === 'undefined' || value === 'undefined undefined' ||
      (typeof value === 'string' && value.trim() === '')
  }

  // Helper function to get valid value or dash
  const getValidValue = (value) => isEmpty(value) ? '-' : value

  // Helper: pick first non-empty, non-placeholder value
  const pickFirstValid = (...vals) => {
    for (const v of vals) {
      if (!isEmpty(v)) {
        const t = String(v).trim()
        if (t && t !== '-' && t.toLowerCase() !== 'n/a') return t
      }
    }
    return '-'
  }

  // Heuristic extractor: search object up to depth 2 for matching keys
  const extractByKeyPattern = (obj, pattern, maxDepth = 2) => {
    try {
      const seen = new Set()
      const stack = [{ o: obj, d: 0 }]
      while (stack.length) {
        const { o, d } = stack.pop()
        if (!o || typeof o !== 'object' || d > maxDepth) continue
        if (seen.has(o)) continue
        seen.add(o)
        for (const [k, v] of Object.entries(o)) {
          if (pattern.test(k)) {
            // Direct string
            if (typeof v === 'string' && !isEmpty(v) && v !== '-') return v.trim()
            // Object with common name fields
            if (v && typeof v === 'object') {
              const nameLike = v.name || v.label || v.title || v.text
              if (typeof nameLike === 'string' && !isEmpty(nameLike) && nameLike !== '-') return String(nameLike).trim()
            }
            // Array of objects/strings
            if (Array.isArray(v)) {
              for (const item of v) {
                if (typeof item === 'string' && !isEmpty(item) && item !== '-') return item.trim()
                if (item && typeof item === 'object') {
                  const nameLike2 = item.name || item.label || item.title || item.text
                  if (typeof nameLike2 === 'string' && !isEmpty(nameLike2) && nameLike2 !== '-') return String(nameLike2).trim()
                }
              }
            }
          }
          if (v && typeof v === 'object') stack.push({ o: v, d: d + 1 })
        }
      }
    } catch (_) { }
    return '-'
  }

  // Extract degree from various possible shapes
  const extractDegree = (s) => {
    // Common explicit keys first
    const direct = pickFirstValid(
      s.degree,
      s.degreeName,
      s.degree_name,
      s.education?.degree,
      s.education?.degreeName,
      s.educationDetails?.degree,
      s.educationDetails?.degreeName,
      s.academics?.degree,
      s.academics?.degreeName
    )
    if (direct && direct !== '-') return direct
    // Array-based education shapes
    const arrBased = pickFirstValid(
      // common arrays
      Array.isArray(s.education) ? extractByKeyPattern({ education: s.education }, /degree(Name)?/i) : '-',
      Array.isArray(s.educations) ? extractByKeyPattern({ educations: s.educations }, /degree(Name)?/i) : '-',
      Array.isArray(s.educationDetails) ? extractByKeyPattern({ educationDetails: s.educationDetails }, /degree(Name)?/i) : '-',
      Array.isArray(s.academics) ? extractByKeyPattern({ academics: s.academics }, /degree(Name)?/i) : '-'
    )
    if (arrBased && arrBased !== '-') return arrBased
    // Heuristic scan
    const fromScan = pickFirstValid(
      extractByKeyPattern(s, /(^|_|\.)degree(Name)?$/i),
      extractByKeyPattern(s, /deg(r)?(ee)?/i)
    )
    return fromScan
  }

  // Extract department from various possible shapes
  const extractDepartment = (s) => {
    const direct = pickFirstValid(
      s.department,
      s.departmentName,
      s.department_name,
      s.dept,
      s.branch,
      s.education?.department,
      s.education?.departmentName,
      s.educationDetails?.department,
      s.educationDetails?.departmentName,
      s.academics?.department,
      s.academics?.departmentName
    )
    if (direct && direct !== '-') return direct
    // Array-based education shapes
    const arrBased = pickFirstValid(
      Array.isArray(s.education) ? extractByKeyPattern({ education: s.education }, /department(Name)?|dept|branch/i) : '-',
      Array.isArray(s.educations) ? extractByKeyPattern({ educations: s.educations }, /department(Name)?|dept|branch/i) : '-',
      Array.isArray(s.educationDetails) ? extractByKeyPattern({ educationDetails: s.educationDetails }, /department(Name)?|dept|branch/i) : '-',
      Array.isArray(s.academics) ? extractByKeyPattern({ academics: s.academics }, /department(Name)?|dept|branch/i) : '-'
    )
    if (arrBased && arrBased !== '-') return arrBased
    const fromScan = pickFirstValid(
      extractByKeyPattern(s, /(^|_|\.)department(Name)?$/i),
      extractByKeyPattern(s, /(dept|branch)/i)
    )
    return fromScan
  }

  // Helper: parse various date formats to DD/MM/YYYY
  const toDDMMYYYY = (val) => {
    if (isEmpty(val) || val === '-') return '-'
    const str = String(val).trim()
    const formats = [
      'DD MMM YYYY',
      'DD MMM YYYY, h:mm A',
      'DD MMM YYYY, H:mm',
      'YYYY-MM-DD',
      moment.ISO_8601,
    ]
    for (const fmt of formats) {
      const m = fmt === moment.ISO_8601 ? moment(str) : moment(str, fmt, true)
      if (m.isValid()) return m.format('DD/MM/YYYY')
    }
    // Last resort: Date.parse
    const d = new Date(str)
    if (!isNaN(d.getTime())) {
      return moment(d).format('DD/MM/YYYY')
    }
    return '-'
  }

  // Helper function to render CEFR levels with conditional display
  const renderCefrLevel = (primary, secondary) => {
    const validPrimary = primary && primary !== '-'
    const validSecondary = secondary && secondary !== '-'

    if (!validPrimary && !validSecondary) {
      return <span style={{ textAlign: 'center', display: 'block' }}>-</span>
    }

    return (
      <CefrLevel>
        {validPrimary && <PrimaryCefr>{primary}</PrimaryCefr>}
        {validSecondary && <SecondaryCefr>{secondary}</SecondaryCefr>}
      </CefrLevel>
    )
  }

  const formattedData = useMemo(() => {
    // NOTE: All filtering (domain, degree, department, specialization, passingYear, search,
    // CEFR, aptitude, consistency) is now handled SERVER-SIDE in the API call.
    // This matches the pattern in DiagnosisList where we use data directly without re-filtering.
    // See AssessmentDetails.js -> fetchSpecificAssessmentStudentList for the API params.
    let filteredData = data || []

    console.log('[CandidateList] Data Debug (no client-side filtering):')
    console.log('  - Raw data length:', filteredData.length)
    console.log('  - Raw data sample:', filteredData[0])

    // Client-side filters REMOVED - all filtering is done server-side now
    // Previously filtered by: topLevelDomain, topLevelDegree, topLevelDepartment,
    // topLevelSpecialization, topLevelPassingYear, searchTerm
    // These are now sent as API params and filtered in the backend

    console.log('Final data length (no client filter applied):', filteredData.length)

    // Transform the filtered data for table display
    const mapped = filteredData.map(student => {
      // --- Derive CEFR (communication) robustly ---
      const deriveCefr = s => {
        const isValid = v => v && String(v).trim() !== '-' && String(v).trim().toLowerCase() !== 'n/a'
        const pick = (...vals) => {
          for (const v of vals) {
            if (isValid(v)) return String(v).trim()
          }
          return '-'
        }
        // Various possible shapes
        const fromAssigned = s.assignedcefr || s.assignedCEFR || s.assignedCefr || s.assigned_cefr || s.AssignedCEFR
        let primary = '-'
        let secondary = '-'
        if (fromAssigned && typeof fromAssigned === 'object') {
          primary = pick(
            fromAssigned.primary,
            fromAssigned.Primary,
            fromAssigned.P,
            fromAssigned.main,
            fromAssigned.primaryLevel,
            fromAssigned.primary_level,
            fromAssigned.level_primary
          )
          secondary = pick(
            fromAssigned.secondary,
            fromAssigned.Secondary,
            fromAssigned.S,
            fromAssigned.backup,
            fromAssigned.secondaryLevel,
            fromAssigned.secondary_level,
            fromAssigned.level_secondary
          )
        }
        // Direct fields
        primary = pick(
          primary,
          s.cefrPrimary,
          s.cefr_primary,
          s.primaryCefr,
          s.primary_cefr,
          s.assigned_cefr_primary,
          s.assignedCefrPrimary,
          s.assignedCEFRPrimary
        )
        secondary = pick(
          secondary,
          s.cefrSecondary,
          s.cefr_secondary,
          s.secondaryCefr,
          s.secondary_cefr,
          s.assigned_cefr_secondary,
          s.assignedCefrSecondary,
          s.assignedCEFRSecondary
        )
        // Single-level fields (treat as primary)
        const single = pick(
          s.cefr,
          s.CEFR,
          s.cefrLevel,
          s.cefr_level,
          s.currentCEFR,
          s.current_cefr,
          s.overallCEFR,
          s.overallCefr,
          s.communicationCEFR,
          s.communication_cefr
        )
        if (isValid(single) && !isValid(primary)) {
          primary = single
        }
        // Scores container possibility
        const comm = s.communicationScores || s.commScores || s.scores || s.communication
        if (comm && typeof comm === 'object') {
          const p2 = pick(comm.primaryCefr, comm.primary, comm.cefrPrimary, comm.primary_level, comm.primaryLevel)
          const s2 = pick(comm.secondaryCefr, comm.secondary, comm.cefrSecondary, comm.secondary_level, comm.secondaryLevel)
          if (isValid(p2) && !isValid(primary)) primary = p2
          if (isValid(s2) && !isValid(secondary)) secondary = s2
          const single2 = pick(comm.overallCEFR, comm.cefr, comm.overallCefr)
          if (isValid(single2) && !isValid(primary)) primary = single2
        }

        // Check if `currentLevel` looks like CEFR (A1-C2) and use it if primary is missing
        if (!isValid(primary) && isValid(s.currentLevel)) {
          const val = String(s.currentLevel).trim().toUpperCase()
          const cefrSet = new Set(['A1', 'A2', 'B1', 'B2', 'C1', 'C2', '-'])
          if (cefrSet.has(val)) {
            primary = val
          }
        }
        // Heuristic: If still not found, scan shallow fields and one-level nested objects
        if (!isValid(primary)) {
          try {
            const allowed = new Set(['A1', 'A2', 'B1', 'B2', 'C1', 'C2', '-'])
            const scanObj = (obj) => {
              if (!obj || typeof obj !== 'object') return null
              for (const [k, v] of Object.entries(obj)) {
                if (v && typeof v === 'object') {
                  const nested = scanObj(v)
                  if (nested) return nested
                } else if (typeof v === 'string') {
                  const keyHit = /cefr|level|communication/i.test(k) && !/assigned/i.test(k)
                  const val = v.trim().toUpperCase()
                  if (keyHit && allowed.has(val)) return val
                }
              }
              return null
            }
            const guessed = scanObj(s)
            if (guessed) primary = guessed
          } catch (_) { }

          // Reorder for display: if CEFR filter is active and matches secondary, swap so UI shows the matched level first
          try {
            const typeLower = (assessmentType || '').toLowerCase()
            const filterCefr = (chartFilters?.cefr || '').toString().toUpperCase()
            const p = (cefr.primary || '').toString().toUpperCase()
            const s = (cefr.secondary || '').toString().toUpperCase()
            if (typeLower.includes('communication') && filterCefr && p !== filterCefr && s === filterCefr) {
              cefr = { primary: s, secondary: p }
              console.debug('[CandidateList] Reordered CEFR for display based on active filter', {
                id: student.id || student.assessmentAssignedId || student.studentId,
                primary: cefr.primary,
                secondary: cefr.secondary,
              })
            }
          } catch (_) { }
        }
        // Final sanitize: only allow known CEFR values if present
        const allowed = new Set(['A1', 'A2', 'B1', 'B2', 'C1', 'C2'])
        const norm = v => {
          const t = String(v || '-').toUpperCase().trim()
          return allowed.has(t) ? t : (t === '-' ? '-' : t)
        }
        return { primary: norm(primary), secondary: norm(secondary) }
      }
      // --- Derive taken and total assessments robustly ---
      // Note: The column displays "taken / sent" so we return { taken, total (sent) }
      const deriveSkipsAndTotal = s => {
        // 0) Explicit Sent/Taken from new API (Prioritize this!)
        if (Number.isFinite(Number(s.assessmentsSent))) {
          const sent = Number(s.assessmentsSent)
          // If assessmentsTaken is missing, assume 0.
          const taken = Number.isFinite(Number(s.assessmentsTaken)) ? Number(s.assessmentsTaken) : 0
          // Return taken and sent directly (not skips) since column shows "taken / sent"
          return { skips: String(taken), total: String(sent) }
        }



        // 1) Direct string like "x/y"
        const rawSkipCount = s.skipCount || s.assessmentSkipCount || s.skips || s.assessmentSkips
        if (typeof rawSkipCount === 'string' && rawSkipCount.includes('/')) {
          const parts = rawSkipCount.split('/')
          const a = (parts[0] || '').trim()
          const b = (parts[1] || '').trim()
          return { skips: a || '0', total: b || '0' }
        }

        // 2) Separate numeric fields
        const totalAssessments =
          s.totalAssessments || s.total_assessments || s.assessmentsCount || s.total || s.total_count
        const skipNum =
          s.skip || s.skips || s.noOfSkips || s.numberOfSkips || s.assessmentSkipCount

        if (Number.isFinite(Number(skipNum)) && Number.isFinite(Number(totalAssessments))) {
          return { skips: String(Number(skipNum)), total: String(Number(totalAssessments)) }
        }

        // 3) Compute from assigned vs attempted/completed
        const assigned =
          s.assessmentsSent || s.assessmentAssignedCount || s.assessment_assigned_count || s.assignedCount || s.assigned || s.assessmentAssigned
        const attempted =
          s.assessmentsTaken || s.assessmentAttemptedCount || s.assessment_attempted_count || s.attemptedCount || s.attempted || s.assessmentAttempted
        const completed = s.completedCount || s.completed || s.assessmentCompleted

        if (Number.isFinite(Number(assigned))) {
          const baseTotal = Number(assigned)
          const done = Number.isFinite(Number(attempted))
            ? Number(attempted)
            : Number.isFinite(Number(completed))
              ? Number(completed)
              : null
          if (done !== null) {
            const calcSkips = Math.max(0, baseTotal - done)
            return { skips: String(calcSkips), total: String(baseTotal) }
          }
        }

        // 4) Fallback: if we only have a total and some kind of done count
        if (Number.isFinite(Number(totalAssessments))) {
          const done2 = Number.isFinite(Number(attempted))
            ? Number(attempted)
            : Number.isFinite(Number(completed))
              ? Number(completed)
              : null
          if (done2 !== null) {
            const calcSkips = Math.max(0, Number(totalAssessments) - done2)
            return { skips: String(calcSkips), total: String(Number(totalAssessments)) }
          }
        }

        // 5) Last resort: treat a numeric rawSkipCount with unknown total
        if (Number.isFinite(Number(rawSkipCount))) {
          return { skips: String(Number(rawSkipCount)), total: '0' }
        }

        return { skips: '0', total: '0' }
      }

      const { skips, total } = deriveSkipsAndTotal(student)

      // Handle skipCount string format directly from API
      let finalSkips = skips
      let finalTotal = total
      if (student.skipCount && typeof student.skipCount === 'string' && student.skipCount.includes('/')) {
        const parts = student.skipCount.split('/')
        finalSkips = (parts[0] || '').trim() || '0'
        finalTotal = (parts[1] || '').trim() || '0'
      }

      if (finalSkips === '0' && finalTotal === '0') {
        try {
          console.debug('[CandidateList] Skips unresolved (0/0). Inspecting keys:', {
            id: student.id || student.assessmentAssignedId || student.studentId,
            skipCount: student.skipCount,
            keys: Object.keys(student || {}),
          })
        } catch (_) { }
      }

      // Avatar letter logic - prioritize studentName, then name, then email
      let avatarLetter = '-'
      if (!isEmpty(student.studentName)) {
        avatarLetter = student.studentName.trim().charAt(0).toUpperCase()
      } else if (!isEmpty(student.name)) { // Added check for 'name'
        avatarLetter = student.name.trim().charAt(0).toUpperCase()
      } else if (!isEmpty(student.email)) {
        avatarLetter = student.email.trim().charAt(0).toUpperCase()
      }

      // Phone number logic - handle undefined cases and alternate keys
      let phone = student.phone || student.phoneNumber || student.mobile || student.contactNumber || student.mobileNumber // Added mobileNumber
      if (isEmpty(phone) || phone === 'undefined undefined' || phone === '+91 ') {
        phone = '-'
      } else {
        phone = phone.trim()
      }
      // Name logic - prefer studentName, name, fallback to email (so table isn't blank)
      let displayName = '-'
      if (!isEmpty(student.studentName)) {
        displayName = student.studentName.trim()
      } else if (!isEmpty(student.name)) { // Added check for 'name'
        displayName = student.name.trim()
      } else if (!isEmpty(student.email)) {
        displayName = student.email.trim()
      }

      // If no explicit aptitude level but aptitudeScores exist, derive level from overallPercentage
      let derivedAptitudeLevel = '-'
      const overallPct = student?.aptitudeScores?.overallPercentage
      if ((overallPct || overallPct === 0) && !Number.isNaN(Number(overallPct))) {
        const pct = Number(overallPct)
        if (pct >= 70) derivedAptitudeLevel = 'Advanced'
        else if (pct >= 40) derivedAptitudeLevel = 'Intermediate'
        else derivedAptitudeLevel = 'Beginner'
      }

      // If we still don't have a level for aptitude-like records, use a status label
      let aptitudeStatusLabel = ''
      const hasAptitudeContext =
        Object.prototype.hasOwnProperty.call(student, 'aptitudeScores') ||
        Object.prototype.hasOwnProperty.call(student, 'scoresCalculated')
      if (hasAptitudeContext && (derivedAptitudeLevel === '-' || isEmpty(derivedAptitudeLevel))) {
        const hasStartedDate = !!(student.startDate && String(student.startDate).trim() && String(student.startDate).trim() !== '-')
        const started = !!(student.startedDateTime || hasStartedDate)
        const hasDroppedDate = !!(student.droppedOffDateTime && String(student.droppedOffDateTime).trim() && String(student.droppedOffDateTime).trim() !== '-')
        const dropped = !!hasDroppedDate
        const scored = student.scoresCalculated === true
        try {
          console.debug('[CandidateList] Aptitude status computed', {
            id: student.id || student.assessmentAssignedId || student.studentId,
            scoresCalculated: student.scoresCalculated,
            startedDateTime: student.startedDateTime,
            startDate: student.startDate,
            droppedOffDateTime: student.droppedOffDateTime,
            derivedAptitudeLevel,
            aptitudeStatusLabel,
          })
        } catch (_) { }
      }

      let cefr = deriveCefr(student)
      // If communication assessment and a CEFR chart filter is active, but we still couldn't derive CEFR,
      // use the selected CEFR filter value as a safe fallback so the table reflects the filtered level.
      try {
        const typeLower = (assessmentType || '').toLowerCase()
        const filterCefr = chartFilters?.cefr
        if (
          typeLower.includes('communication') &&
          filterCefr &&
          (cefr.primary === '-' || !cefr.primary)
        ) {
          cefr = { primary: String(filterCefr).toUpperCase(), secondary: cefr.secondary }
          console.debug('[CandidateList] Applied CEFR fallback from chart filter', {
            id: student.id || student.assessmentAssignedId || student.studentId,
            fallbackTo: cefr.primary,
          })
        }
      } catch (_) { }
      return {
        id: getValidValue(student.id || student.assessmentAssignedId),
        studentNumber: student.studentNumber || null,  // Keep raw value for report download API
        avatar: getValidValue(student.student_img_url || student.profileUrl), // Added profileUrl
        name: displayName,
        phone,
        email: getValidValue(student.email || student.studentEmail || student.emailId),
        degree: getValidValue(extractDegree(student)),
        department: getValidValue(extractDepartment(student)),
        specialization: getValidValue(
          student.specialization ||
          student.specializationName ||
          student.specialization_name ||
          student.major
        ),
        cefrPrimary: cefr.primary,
        cefrSecondary: cefr.secondary,
        // Prefer explicit aptitude level, then derived from percentage, then status
        aptitudeLevel: (() => {
          // 1) Check for explicit aptitude level (ignore '-' placeholders)
          let explicit = pickFirstValid(
            student.aptitudeLevel,
            student.aptitude_level,
            student.proficiencyLevel,
            student.proficiency_level,
            student.current_level,
            student.level
          )


          // Special handling for currentLevel: check if it's NOT a CEFR value before treating as aptitude
          if ((!explicit || explicit === '-') && student.currentLevel) {
            const val = String(student.currentLevel).trim().toUpperCase()
            const cefrSet = new Set(['A1', 'A2', 'B1', 'B2', 'C1', 'C2'])
            if (!cefrSet.has(val)) {
              explicit = student.currentLevel // It's likely aptitude (e.g. "Beginner")
            }
          }
          if (explicit && explicit !== '-') return explicit

          // 2) Derive from aptitudeScores if available
          if (derivedAptitudeLevel && derivedAptitudeLevel !== '-') return derivedAptitudeLevel

          // 3) Fall back to status only if no explicit level and no scores
          return aptitudeStatusLabel || '-'
        })(),
        // Derive assessment date from multiple possible fields, skipping '-' placeholders
        assessmentDate: pickFirstValid(
          // Prefer sentDate first so pending/sent rows always show a date
          student.sentDate,
          student.sent_date,
          student.assessmentDate,
          student.assessment_date,
          student.completedOn,
          student.completed_on,
          student.attemptedOn,
          student.attempted_on,
          student.startedAt,
          student.startedDateTime,
          student.startDate,
          student.droppedOffDateTime
        ),
        assessmentSkips: finalSkips,
        totalAssessments: finalTotal,
        skipCountRaw: student.skipCount || student.assessmentSkips || student.assessmentSkipCount || student.skips,
        avatarLetter,
        assessmentAssignedId: student.assessmentAssignedId,
        // Score fields from backend
        totalScore: student.totalScore ?? null,
        sectionScores: student.sectionScores ?? null,
        aptitudeScores: student.aptitudeScores ?? null,
        roleBasedScores: student.roleBasedScores ?? null,
        customAssessmentScores: student.customAssessmentScores ?? student.customassessmentscores ?? null,
        // Friendly CEFR name from backend (e.g., "A1-Beginner")
        currentLevelDisplay: student.currentLevelDisplay || null,
        // Proctoring status from backend
        proctoring: student.proctoring || '-',
        submittedAt: student.submittedAt,
        // Scored flag + AI Interview outcome: resolveAttemptStatus reads them to
        // tell "Completed" from "Preparing Report" / "Report Delayed".
        isScoreCalculated: student.isScoreCalculated ?? student.scoresCalculated,
        aiInterviewScores: student.aiInterviewScores,
        // New fields
        mobileNumber: student.mobileNumber,
        status: student.status,
        assignedDifficulty: student.assignedLevel,
        // Assessment type for drawer detection
        assessmentType: student.assessmentType || assessmentType || null,
        // Add the original student object to pass to the report drawer
        originalData: student,
      }
    })

    try {
      const sample = mapped.slice(0, 5).map(r => ({
        id: r.id,
        name: r.name,
        degree: r.degree,
        department: r.department,
        aptitudeLevel: r.aptitudeLevel,
        isAptitudeType: (assessmentType || '').toLowerCase().includes('aptitude'),
        cefrPrimary: r.cefrPrimary,
        cefrSecondary: r.cefrSecondary,
        scoresCalculated: r.originalData?.scoresCalculated,
        startedDateTime: r.originalData?.startedDateTime,
        startDate: r.originalData?.startDate,
        droppedOffDateTime: r.originalData?.droppedOffDateTime,
        assessmentDate: r.assessmentDate,
        parsedDate: r.assessmentDate ? toDDMMYYYY(r.assessmentDate) : '-',
        skipCountRaw: r.originalData?.skipCount,
        computedSkips: `${r.assessmentSkips}/${r.totalAssessments}`,
        // Add raw data inspection
        rawDegree: r.originalData?.degree,
        rawDepartment: r.originalData?.department,
        rawSkipCount: r.originalData?.skipCount,
        rawAptitudeLevel: r.originalData?.aptitudeLevel,
        rawAptitudeScores: r.originalData?.aptitudeScores,
        rawAssignedCefr: r.originalData?.assignedcefr || r.originalData?.assignedCEFR || r.originalData?.assignedCefr || r.originalData?.assigned_cefr,
        rawCefrPrimary: r.originalData?.cefrPrimary || r.originalData?.cefr_primary,
        rawCefrSecondary: r.originalData?.cefrSecondary || r.originalData?.cefr_secondary,
        rawSingleCefr: r.originalData?.cefr || r.originalData?.CEFR || r.originalData?.cefrLevel || r.originalData?.overallCEFR,
        allRawKeys: Object.keys(r.originalData || {}),
      }))
      console.log('[CandidateList] Mapped sample with raw data inspection:')
      console.table(sample)
    } catch (_) { }

    return mapped
  }, [
    data,
    assessmentType,
    chartFilters, // Keep for CEFR/aptitude display logic, not filtering
  ])

  console.log('[CandidateList] Sample mapped rows:', formattedData.slice(0, 3).map(r => ({
    name: r.name,
    degree: r.degree,
    department: r.department,
    rawDegree: r.originalData?.degree || r.originalData?.degreeName || r.originalData?.degree_name,
    rawDepartment: r.originalData?.department || r.originalData?.departmentName || r.originalData?.department_name || r.originalData?.dept || r.originalData?.branch,
  })));

  // Effect for MultiFilter pills (from pop-up menu)
  useEffect(() => {
    const pills = []
    domain.forEach(v =>
      pills.push({ nameValue: `Domain - ${v}`, type: 'domain', value: v })
    )
    degree.forEach(v =>
      pills.push({ nameValue: `Degree - ${v}`, type: 'degree', value: v })
    )
    department.forEach(v =>
      pills.push({ nameValue: `Dept - ${v}`, type: 'department', value: v })
    )
    specialization.forEach(v =>
      pills.push({ nameValue: `Spec - ${v}`, type: 'specialization', value: v })
    )
    passingYear.forEach(v =>
      pills.push({ nameValue: `Year - ${v}`, type: 'passingYear', value: v })
    )
    setMultiSelect(pills)
  }, [domain, degree, department, specialization, passingYear])

  // Enhanced filter array with individual domain names
  const filterArr = useMemo(() => {
    const chips = []

    // Show individual domain names instead of just "Domain"
    topLevelDomain.forEach(item =>
      chips.push({
        label: item.label, // Show the actual domain name
        value: item.label,
        id: `domain_${item.value}`,
      })
    )

    topLevelDegree.forEach(item =>
      chips.push({
        label: item.label, // Show the actual degree name
        value: item.label,
        id: `degree_${item.value}`,
      })
    )

    topLevelDepartment.forEach(item =>
      chips.push({
        label: item.label, // Show the actual department name
        value: item.label,
        id: `department_${item.value}`,
      })
    )

    topLevelSpecialization.forEach(item =>
      chips.push({
        label: item.label, // Show the actual specialization name
        value: item.label,
        id: `specialization_${item.value}`,
      })
    )

    if (topLevelPassingYear) {
      chips.push({
        label: topLevelPassingYear, // Show the actual year
        value: topLevelPassingYear,
        id: `year_${topLevelPassingYear}`,
      })
    }

    if (showStatusFilter && Array.isArray(statusFilter)) {
      statusFilter.forEach(value => {
        const label = STATUS_LABEL_MAP[value] || value
        chips.push({
          label: `Status: ${label}`,
          value,
          id: `status_${value}`,
        })
      })
    }

    return chips
  }, [
    topLevelDomain,
    topLevelDegree,
    topLevelDepartment,
    topLevelSpecialization,
    topLevelPassingYear,
    statusFilter,
    showStatusFilter,
  ])

  // 1. Add state to manage the visibility of the filter chip modal
  const [isChipModalVisible, setIsChipModalVisible] = useState(false)

  // 2. Add a handler to toggle the modal's visibility
  const handleChipModalToggle = () => {
    setIsChipModalVisible(prev => !prev)
  }

  const handleRemoveFilterChip = chip => {
    const [type, ...valueParts] = chip.id.split('_')
    const value = valueParts.join('_')

    switch (type) {
      case 'domain':
        setTopLevelDomain(topLevelDomain.filter(item => item.value !== value))
        break
      case 'degree':
        setTopLevelDegree(topLevelDegree.filter(item => item.value !== value))
        break
      case 'department':
        setTopLevelDepartment(
          topLevelDepartment.filter(item => item.value !== value)
        )
        break
      case 'specialization':
        setTopLevelSpecialization(
          topLevelSpecialization.filter(item => item.value !== value)
        )
        break
      case 'year':
        setTopLevelPassingYear(null)
        break
      case 'status':
        if (typeof setStatusFilter === 'function') {
          setStatusFilter((statusFilter || []).filter(v => v !== value))
          if (onPageChange) onPageChange(1)
        }
        break
      default:
        break
    }
  }

  const handleMenuOpen = () => {
    setTempDomain(domain)
    setTempDegree(degree)
    setTempDepartment(department)
    setTempSpecialization(specialization)
    setTempPassingYear(passingYear)
    setIsMenuOpen(true)
  }

  const handleApplyFilter = () => {
    setDomain(tempDomain)
    setDegree(tempDegree)
    setDepartment(tempDepartment)
    setSpecialization(tempSpecialization)
    setPassingYear(tempPassingYear)
    setIsMenuOpen(false)
  }

  const handleFilterCancel = () => {
    setIsMenuOpen(false)
  }

  const handleMultiFilter = removedPillValues => {
    const removedPills = multiSelect.filter(
      pill => !removedPillValues.includes(pill.nameValue)
    )
    if (removedPills.length > 0) {
      const removed = removedPills[0]
      switch (removed.type) {
        case 'domain':
          setDomain(prev => prev.filter(item => item !== removed.value))
          break
        case 'degree':
          setDegree(prev => prev.filter(item => item !== removed.value))
          break
        case 'department':
          setDepartment(prev => prev.filter(item => item !== removed.value))
          break
        case 'specialization':
          setSpecialization(prev => prev.filter(item => item !== removed.value))
          break
        case 'passingYear':
          setPassingYear(prev => prev.filter(item => item !== removed.value))
          break
        default:
          break
      }
    }
  }

  const onSorting = (pagination, filters, sorter) => {
    // Map antd field names to API field names
    const fieldMap = {
      name: 'name',
      assessmentDate: 'startedAt',
    }

    if (sorter.order) {
      const sortConfig = {
        field: fieldMap[sorter.field] || sorter.field,
        order: sorter.order === 'ascend' ? 'asc' : 'desc'
      }
      setSortBy(sortConfig)
      // Call parent callback for server-side sorting
      if (onSortChange) {
        onSortChange(sortConfig)
      }
    } else {
      setSortBy({})
      if (onSortChange) {
        onSortChange({ field: 'name', order: 'asc' }) // Default sort
      }
    }
  }

  // Helper function to render contact info with conditional display
  const renderContactInfo = (phone, email) => {
    const validPhone = phone !== '-'
    const validEmail = email !== '-'

    if (!validPhone && !validEmail) {
      return <span>-</span>
    }

    return (
      <ContactInfo>
        {/* {validPhone && (
            <span>{phone}</span>
        )} */}
        {validEmail && (
          <span>{email}</span>
        )}
      </ContactInfo>
    )
  }

  const renderDegreeDeptInfo = (degree, department) => {
    const validDegree = degree !== '-'
    const validDepartment = department !== '-'

    if (!validDegree && !validDepartment) {
      return <span style={{ display: 'block', textAlign: 'center' }}>-</span>
    }

    return (
      <ContactInfo>
        {validDegree && (
          <span>{degree}</span>
        )}
        {validDepartment && (
          <span>{department}</span>
        )}
      </ContactInfo>
    )
  }

  const columns = [
    {
      title: 'Candidate',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 250,
      fixed: 'left',
      render: (text, record) => {
        const hasName = record.name && record.name !== '-'
        const hasEmail = record.email && record.email !== '-'
        let phone = record.mobileNumber
        const p = String(phone || '').trim()
        if (!p || /^(?:\+?91)?[\s-]*$/.test(p)) {
          phone = '-'
        }
        const hasPhone = phone && phone !== '-'

        if (!hasName && !hasEmail) {
          return (
            <span style={{ display: 'block', textAlign: 'center' }}>-</span>
          )
        }

        return (
          <CandidateInfo
            onClick={() => {
              if (onCandidateClick) {
                onCandidateClick(record)
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div>
              {hasName && <CandidateName>{record.name}</CandidateName>}
              {hasEmail && <CandidateId style={{ marginBottom: 4 }}>{record.email}</CandidateId>}
              {hasPhone && <ContactInfo><span style={{ fontSize: '12px', color: '#6b7280' }}>{phone}</span></ContactInfo>}
            </div>
          </CandidateInfo>
        )
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      align: 'center',
      render: (val, record) => {
        const key = resolveAttemptStatus(record)
        // Unknown backend values are shown as sent rather than guessed.
        const status = ATTEMPT_STATUS[key] || { label: val, color: '#6b7280' }
        const label = (
          <span style={{ color: status.color, fontWeight: 600 }}>{status.label || '-'}</span>
        )
        return ATTEMPT_STATUS_TOOLTIP[key]
          ? <Tooltip title={ATTEMPT_STATUS_TOOLTIP[key]}><span tabIndex={0}>{label}</span></Tooltip>
          : label
      }
    },
    ...(['communication', 'aptitude'].some(t => (assessmentType || '').toLowerCase().includes(t)) ? [{
      title: 'Assigned Difficulty',
      dataIndex: 'assignedDifficulty',
      key: 'assignedDifficulty',
      width: 150,
      align: 'center',
      render: val => {
        if (!val || val === '-') return '-'
        const display = val.charAt(0).toUpperCase() + val.slice(1);
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '12px',
            background: '#f3f4f6',
            color: '#374151',
            fontWeight: 600,
            fontSize: '12px'
          }}>
            {display}
          </span>
        )
      }
    }] : []),
    {
      title: 'Degree & Department',
      key: 'degreeDept',
      width: 200,
      render: (_, record) => renderDegreeDeptInfo(record.degree, record.department),
    },
    ...(['communication', 'aptitude'].some(t => (assessmentType || '').toLowerCase().includes(t)) ? [{
      title: 'Progression Level',
      dataIndex: 'currentLevel',
      key: 'currentLevel',
      width: 140,
      align: 'center',
      sorter: true,
      render: (_, record) => {
        const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1) : str

        const typeLower = (assessmentType || '').toLowerCase()
        const isCommunication = typeLower.includes('communication')
        const isAptitude = typeLower.includes('aptitude')
        if (isCommunication) {
          let displayLevel = record.currentLevelDisplay || record.cefrPrimary
          if (displayLevel && displayLevel !== '-') {
            displayLevel = capitalize(displayLevel)
            return <LevelBadge level={displayLevel}>{displayLevel}</LevelBadge>
          }
          return renderCefrLevel(record.cefrPrimary, record.cefrSecondary)
        }
        if (isAptitude) {
          if (record.currentLevelDisplay && record.currentLevelDisplay !== '-') {
            return <LevelBadge level={record.currentLevelDisplay}>{record.currentLevelDisplay}</LevelBadge>
          }

          let level = record.aptitudeLevel && record.aptitudeLevel !== '-' ? record.aptitudeLevel : (record.currentLevel && record.currentLevel !== '-' ? record.currentLevel : null)
          if (!level && record.originalData) {
            const od = record.originalData
            const pct = od?.aptitudeScores?.overallPercentage
            if ((pct || pct === 0) && !Number.isNaN(Number(pct))) {
              const p = Number(pct)
              level = p >= 70 ? 'Advanced' : p >= 40 ? 'Intermediate' : 'Beginner'
            }
          }
          const displayLevel = level ? capitalize(level) : null
          return displayLevel ? <LevelBadge level={displayLevel}>{displayLevel}</LevelBadge> : '-'
        }
        return <span style={{ textAlign: 'center', display: 'block' }}>-</span>
      },
    }] : []),
    {
      title: 'Assmt. Taken On',
      dataIndex: 'assessmentDate',
      key: 'assessmentDate',
      sorter: true,
      width: 150,
      align: 'center',
      render: text => toDDMMYYYY(text),
    },
    {
      title: 'Assmts. taken / sent',
      key: 'assessmentSkips',
      width: 180,
      align: 'center',
      render: (_, record) => {
        const raw = record.skipCountRaw || record.originalData?.skipCount || record.originalData?.assessmentSkipCount || record.originalData?.skips || record.originalData?.assessmentSkips
        if (typeof raw === 'string' && raw.includes('/')) {
          return <span style={{ fontWeight: 600 }}>{raw}</span>
        }
        if (record.assessmentSkips === '0' && record.totalAssessments === '0') {
          return <span style={{ display: 'block', textAlign: 'center' }}>-</span>
        }
        return (
          <span style={{ fontWeight: 600 }}>
            {`${record.assessmentSkips || '-'} / ${record.totalAssessments || '-'}`}
          </span>
        )
      },
    },
    ...(() => {
      const typeLower = (assessmentType || '').toLowerCase();
      const isAptitude = typeLower.includes('aptitude');
      const isCommunication = typeLower.includes('communication');
      const isRoleBased = typeLower.includes('role');
      const isBehavior = typeLower.includes('behavior');
      const isCustom = typeLower.includes('custom');
      const cols = [];

      // Total Score column for all types that have scores
      cols.push({
        title: isCustom ? 'Total Percentage' : 'Total Score',
        key: 'totalScore',
        width: 120,
        dataIndex: 'totalScore',
        align: 'center',
        sorter: true,
        render: (_, r) => {
          const customScores = r.customAssessmentScores || r.customassessmentscores
          const candidates = [r.totalScore, r.totalAvgScore, r.roleBasedScores?.overallScore, customScores?.percentage, r.scores?.totalScore]
          const score = candidates.find(v => typeof v === 'number')
          return score != null ? <span style={{ fontWeight: 600 }}>{Math.round(score * 100) / 100}</span> : '-'
        }
      });

      if (isAptitude) {
        ['Critical', 'Quantitative', 'Logical'].forEach(sec => {
          cols.push({
            title: sec,
            dataIndex: sec.toLowerCase(),
            key: sec.toLowerCase(),
            width: 110,
            align: 'center',
            sorter: true,
            render: (_, r) => r.sectionScores?.[sec.toLowerCase()] != null ? r.sectionScores[sec.toLowerCase()] : '-'
          });
        });
      } else if (isCommunication) {
        const allSections = ['Reading', 'Listening', 'Speaking', 'Writing'];
        const SUBSECTION_TO_GROUP = {
          'Paragraph Reading': 'Reading', 'Audio Question': 'Listening',
          'Video Response': 'Speaking', 'Question Based Response': 'Writing',
          'Email Writing': 'Writing', 'Dictation': 'Writing',
          'Sentence Completion': 'Writing', 'Sentence Build': 'Writing',
        };
        const normalizedEnabled = enabledSections && enabledSections.length > 0
          ? [...new Set(enabledSections.map(s => SUBSECTION_TO_GROUP[s] || s))]
          : allSections;
        allSections.filter(sec => normalizedEnabled.includes(sec)).forEach(sec => {
          cols.push({
            title: sec,
            dataIndex: sec.toLowerCase(),
            key: sec.toLowerCase(),
            width: 110,
            align: 'center',
            sorter: true,
            render: (_, r) => r.sectionScores?.[sec.toLowerCase()] != null ? r.sectionScores[sec.toLowerCase()] : '-'
          });
        });
      } else if (isRoleBased) {
        // Role-based columns = the SELECTED sections (question count > 0 at
        // creation), driven by `roleBasedSections` from the API so they show even
        // before scores are calculated. Falls back to the score keys present in
        // the rows (excluding video metadata) if the section list is missing.
        const sectionDefs = resolveRoleBasedSectionColumns(roleBasedSections, data);
        sectionDefs.forEach(def => {
          cols.push({
            title: def.label,
            key: `section_${def.key}`,
            width: 130,
            align: 'center',
            render: (_, r) => {
              const val = r.roleBasedScores?.[def.key];
              if (val == null || typeof val !== 'number') return '-';
              return <span style={{ fontWeight: 600 }}>{Math.round(val * 100) / 100}</span>;
            },
          });
        });
      } else if (isBehavior) {
        // Behavior competencies are fully dynamic — derive from the row data.
        const allKeys = new Set();
        const capitalize = (str) => str.replace(/Score$/, '').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        (data || []).forEach(s => {
          const scores = s.sectionScores || s.roleBasedScores;
          if (scores && typeof scores === 'object') {
            Object.keys(scores).forEach(k => {
              if (k !== 'overallScore') allKeys.add(k);
            });
          }
        });
        Array.from(allKeys).slice(0, 6).forEach(key => {
          cols.push({
            title: capitalize(key),
            key: `section_${key}`,
            width: 130,
            align: 'center',
            render: (_, r) => {
              const val = r.sectionScores?.[key] ?? r.roleBasedScores?.[key];
              if (val == null || typeof val !== 'number') return '-';
              return <span style={{ fontWeight: 600 }}>{Math.round(val * 100) / 100}</span>;
            },
          });
        });
      } else if (isCustom) {
        // Custom assessment: show Marks (gained/total) and section-wise percentages
        const capitalize = (str) => str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        const getCustomScores = (r) => r.customAssessmentScores || r.customassessmentscores;
        const getSectionStats = (scores) => scores?.sectionWiseStats || scores?.sectionwisestats;
        cols.push({
          title: 'Total Score',
          key: 'customMarks',
          width: 120,
          align: 'center',
          render: (_, r) => {
            const scores = getCustomScores(r);
            const gained = scores?.gainedMarks ?? scores?.gainedmarks;
            const total = scores?.totalMarks ?? scores?.totalmarks;
            if (gained == null || typeof gained !== 'number') return '-';
            return <span style={{ fontWeight: 600 }}>{gained} / {total}</span>;
          }
        });
        // Extract section names from sectionWiseStats
        const allSectionKeys = new Set();
        (data || []).forEach(s => {
          const scores = s.customAssessmentScores || s.customassessmentscores;
          const sectionStats = getSectionStats(scores);
          if (sectionStats && typeof sectionStats === 'object' && typeof sectionStats !== 'string') {
            Object.keys(sectionStats).forEach(k => allSectionKeys.add(k));
          }
        });
        Array.from(allSectionKeys).slice(0, 5).forEach(key => {
          cols.push({
            title: capitalize(key),
            key: `section_${key}`,
            width: 130,
            align: 'center',
            render: (_, r) => {
              const scores = getCustomScores(r);
              const sectionData = getSectionStats(scores)?.[key];
              if (!sectionData || typeof sectionData !== 'object') return '-';
              return sectionData.percentage != null
                ? <span style={{ fontWeight: 600 }}>{Math.round(sectionData.percentage)}%</span>
                : '-';
            },
          });
        });
      }

      return cols;
    })(),
    // Proctoring column at the end
    {
      title: 'Proctoring',
      dataIndex: 'proctoring',
      key: 'proctoring',
      width: 100,
      align: 'center',
      render: (val) => {
        if (!val || val === '-') return '-'
        const isBad = val.toLowerCase() === 'bad' || val.toLowerCase() === 'poor'
        return (
          <span style={{
            color: isBad ? '#ef4444' : '#16a34a',
            fontWeight: 600
          }}>
            {val}
          </span>
        )
      }
    }
  ]

  const filterMenu = (
    <FilterMenuWrapper>
      <Select
        placeholder="Domain"
        mode="multiple"
        value={tempDomain}
        onChange={setTempDomain}
        OptionData={domainOptionsFromProps}
        nameParam="label"
        valueParam="value"
      />
      <Select
        placeholder="Degree"
        mode="multiple"
        value={tempDegree}
        onChange={setTempDegree}
        OptionData={degreeOptionsFromProps}
        nameParam="label"
        valueParam="value"
      />
      <Select
        placeholder="Dept."
        mode="multiple"
        value={tempDepartment}
        onChange={setTempDepartment}
        OptionData={departmentOptionsFromProps}
        nameParam="label"
        valueParam="value"
      />
      <Select
        placeholder="Spec."
        mode="multiple"
        value={tempSpecialization}
        onChange={setTempSpecialization}
        OptionData={specializationOptionsFromProps}
        nameParam="label"
        valueParam="value"
      />
      <Select
        placeholder="Passing Year"
        mode="multiple"
        value={tempPassingYear}
        onChange={setTempPassingYear}
        OptionData={yearOptions}
        nameParam="label"
        valueParam="value"
      />
      <FilterFooter>
        <Button.Secondary text="Cancel" onClick={handleFilterCancel} />
        <Button.Primary text="Apply Filter" onClick={handleApplyFilter} />
      </FilterFooter>
    </FilterMenuWrapper>
  )

  const [domainOptions, setDomainOptions] = useState([])
  const [degreeOptions, setDegreeOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [specializationOptions, setSpecializationOptions] = useState([])
  const [passingYearOptions, setPassingYearOptions] = useState([])

  // --- Data Fetching and Structuring for Filters ---
  // Now uses props from AssessmentDetails which fetches from assessment-specific APIs

  // Build tree data from props (assessment-specific filter options)
  useEffect(() => {
    // Use domain options from props (fetched by AssessmentDetails from assessment-specific API)
    if (domainOptionsFromProps?.length > 0) {
      setDomainOptions(buildTreeData(domainOptionsFromProps, 'Select All Domains'))
    } else {
      setDomainOptions([])
    }
  }, [domainOptionsFromProps])

  useEffect(() => {
    // Use degree options from props
    if (degreeOptionsFromProps?.length > 0) {
      setDegreeOptions(buildTreeData(degreeOptionsFromProps, 'Select All Degrees'))
    } else {
      setDegreeOptions([])
    }
  }, [degreeOptionsFromProps])

  useEffect(() => {
    // Use department options from props
    if (departmentOptionsFromProps?.length > 0) {
      setDepartmentOptions(buildTreeData(departmentOptionsFromProps, 'Select All Depts.'))
    } else {
      setDepartmentOptions([])
    }
  }, [departmentOptionsFromProps])

  useEffect(() => {
    // Use specialization options from props
    if (specializationOptionsFromProps?.length > 0) {
      setSpecializationOptions(buildTreeData(specializationOptionsFromProps, 'Select All Specs.'))
    } else {
      setSpecializationOptions([])
    }
  }, [specializationOptionsFromProps])

  // Still fetch passing years from institute endpoint (not assessment-specific)
  useEffect(() => {
    if (instituteId) {
      dispatch(getYearOfPassing(instituteId)).then(data => {
        setPassingYearOptions(data || [])
      })
    }

    // Always fetch passing years
    dispatch(getYearOfPassing(instituteId)).then(data => {
      setPassingYearOptions(data || [])
    })
  }, [dispatch, instituteId, domainOptionsFromProps])

  // Helper to format assessment type for display
  const getAssessmentTypeDisplay = (type) => {
    const typeMap = {
      communication: 'Communication Assessment',
      behavior: 'Behavioral Assessment',
      aptitude: 'Aptitude',
    }
    return typeMap[type] || type || ''
  }

  return (
    <CandidateListWrapper>
      <HeaderFullWidth>
        <FilterSection>
          <FilterInlineContainer>
            <FilterGrid>


              <FilterGroup>
                <EnhancedFilterLabel>Degree</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={degreeOptions}
                    value={topLevelDegree}
                    onChange={setTopLevelDegree}
                    treeCheckable={true}
                    placeholder="Select Degree"
                    labelInValue
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                  // disabled={topLevelDomain?.length === 0 && domainOptions?.length > 0}
                  />
                </StyledFormItem>
              </FilterGroup>

              <FilterGroup>
                <EnhancedFilterLabel>Department</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={departmentOptions}
                    value={topLevelDepartment}
                    onChange={setTopLevelDepartment}
                    treeCheckable={true}
                    placeholder="Select Department"
                    labelInValue
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    disabled={topLevelDegree?.length === 0 && degreeOptions?.length > 0}
                  />
                </StyledFormItem>
              </FilterGroup>

              <FilterGroup>
                <EnhancedFilterLabel>Specialization</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={specializationOptions}
                    value={topLevelSpecialization}
                    onChange={setTopLevelSpecialization}
                    treeCheckable={true}
                    placeholder="Select Specialization"
                    labelInValue
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    disabled={topLevelDepartment?.length === 0 && departmentOptions?.length > 0}
                  />
                </StyledFormItem>
              </FilterGroup>

              {/* <FilterGroup>
                <StyledFormItem>
                  <Select
                    OptionData={passingYearOptions}
                    value={topLevelPassingYear}
                    onChange={setTopLevelPassingYear}
                    placeholder="Select Year"
                    valueParam="year"
                    nameParam="year"
                    allowClear
                    boxShadow="rgba(50, 73, 215, 0.15) 0px 2px 6px 0px"
                  />
                </StyledFormItem>
              </FilterGroup> */}
            </FilterGrid>
          </FilterInlineContainer>
        </FilterSection>

        {filterArr.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {filterArr.map(chip => (
              <span
                key={chip.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: '#f3f4f6',
                  borderRadius: 16,
                  padding: '4px 12px',
                  marginRight: 8,
                  marginBottom: 8,
                  fontSize: 13,
                  fontWeight: 500,
                  color: '#374151',
                }}
              >
                {chip.label}
                <span
                  onClick={() => handleRemoveFilterChip(chip)}
                  style={{ cursor: 'pointer', color: '#9ca3af', fontWeight: 700 }}
                >
                  x
                </span>
              </span>
            ))}
          </div>
        )}

        <AlignedTableTop>
          <SearchSection style={{ display: 'flex', alignItems: 'center' }}>
            <StyledSearchInput
              placeholder="Search by Candidate Name or Email"
              prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
              value={localSearchTerm}
              onChange={handleSearchChange}
            />
            {showStatusFilter && (
              <Popover
                trigger="click"
                placement="bottomLeft"
                open={statusPopoverOpen}
                onOpenChange={setStatusPopoverOpen}
                overlayClassName="status-filter-popover"
                overlayInnerStyle={{ padding: 10, borderRadius: 10, background: '#ffffff', boxShadow: '0 6px 24px rgba(17, 24, 39, 0.08)' }}
                content={(
                  <StatusPopoverBody>
                    {STATUS_OPTIONS.map(opt => {
                      const checked = tempStatusFilter.includes(opt.value)
                      return (
                        <div
                          key={opt.value}
                          className="status-option"
                          onClick={() => toggleTempStatus(opt.value)}
                        >
                          <span className="label">{opt.label}</span>
                          <Checkbox
                            checked={checked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleTempStatus(opt.value)}
                          />
                        </div>
                      )
                    })}
                    <StatusPopoverFooter>
                      <button type="button" className="clear" onClick={clearStatusFilter}>
                        Clear
                      </button>
                      <button type="button" className="apply" onClick={applyStatusFilter}>
                        <CheckOutlined style={{ fontSize: 12, marginRight: 6 }} />
                        Apply
                      </button>
                    </StatusPopoverFooter>
                  </StatusPopoverBody>
                )}
              >
                <StatusFilterTrigger
                  type="button"
                  $active={Array.isArray(statusFilter) && statusFilter.length > 0}
                >
                  <FilterOutlined />
                  Filter
                  {Array.isArray(statusFilter) && statusFilter.length > 0 && (
                    <Badge
                      count={statusFilter.length}
                      style={{
                        backgroundColor: '#3249d7',
                        marginLeft: 4,
                        boxShadow: 'none',
                      }}
                    />
                  )}
                </StatusFilterTrigger>
              </Popover>
            )}
          </SearchSection>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <ShowingText>
              Showing {formattedData.length} of{' '}
              {totalCount > 0 ? totalCount : formattedData.length}
            </ShowingText>
            <DownloadButton
              icon={<DownloadOutlined />}
              onClick={handleExport}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#3249d7',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                height: '36px',
                fontWeight: 500
              }}
            >
              Export
            </DownloadButton>
          </div>
        </AlignedTableTop>

        <StyledTableSqare
          columns={columns}
          dataSource={formattedData}
          pagination={false}
          rowKey="id"
          loading={loading}
          onChange={onSorting}
          bordered
          scroll={{ x: '1280px' }}
          locale={{
            emptyText: <NoData>No candidates found</NoData>,
          }}
        />
        {totalCount > pageSize && (
          <Pagination
            current={currentPage}
            total={totalCount}
            pageSize={pageSize}
            onChange={onPageChange}
          />
        )}
      </HeaderFullWidth>

    </CandidateListWrapper>
  )
}

export default CandidateList
